import * as _ from 'underscore';

import { ButtonType } from '@tableau/widgets';
import * as React from 'react';
import { ServiceLocator } from '../../app/ServiceLocator';
import { Messages } from '../../codegen/Localize';
import { CheckboxState } from '../../common/components/checkboxes/CheckboxState';
import { ButtonWithSpinner } from '../../common/react/components/ButtonWithSpinner';
import { MultilineTextField } from '../../common/react/components/MultilineTextField';
import { ISiteRoleChanges } from '../../ts/controllers/SiteRolesPickerCtrl';
import { AngularContext, AngularContextFactory } from '../../ts/factories/AngularContextFactory';
import { UsernameHelpers } from '../../ts/helpers/UsernameHelpers';
import { IUserInfo } from '../../ts/models/IUserInfo';
import { Set } from '../../ts/models/Set';
import { ServerUsers } from '../../ts/resources/ServerUsers';
import { IImportUsersFromActiveDirectoryResult } from '../../ts/ServerApi/requests/ImportUsersFromActiveDirectoryRequest';
import { IUpdateServerUsersSiteMembershipResult } from '../../ts/ServerApi/requests/UpdateServerUsersSiteMembershipRequest';
import { IUser } from '../../ts/ServerApi/types/IUser';

import {
  ISiteRoleChange,
  SiteRolesPickerClientService
} from '../../ts/services/SiteRolesPickerClientService';
import { SiteMembershipUpdateActionNotification } from '../services/notifications/SiteMembershipUpdateActionNotification';
import { UserActionNotification, UserActionNotificationParams } from '../services/notifications/UserActionNotification';
import { SiteRolesPickerContainer } from './SiteRolesPickerContainer';

export interface AddActiveDirectoryServerUsersDialogState {
  readonly busy: boolean;
  readonly userInput: string;
}

export interface AddActiveDirectoryServerUsersDialogProps {
  readonly dismiss: () => void;
  readonly close: (result: any) => void;
}

export class AddActiveDirectoryServerUsersDialog extends React.Component<
  AddActiveDirectoryServerUsersDialogProps,
  AddActiveDirectoryServerUsersDialogState
> {
  private dialogScope: ng.IScope = ServiceLocator.getAngularActionContext().scope;
  private angularContext: AngularContext = ServiceLocator.get<AngularContextFactory>('AngularContextFactory').create(this.dialogScope);
  private $q: ng.IQService = this.angularContext.getService('$q');
  private userActionNotification: UserActionNotification = UserActionNotification.instance;
  private serverUsers: ServerUsers = this.angularContext.getService('ServerUsers');
  private rolesPickerClient: SiteRolesPickerClientService = this.angularContext.getService('SiteRolesPickerClientService');
  private siteMembershipUpdateActionNotification: SiteMembershipUpdateActionNotification = SiteMembershipUpdateActionNotification.instance;
  public siteRoleChanges: ISiteRoleChanges;

  public constructor(props: AddActiveDirectoryServerUsersDialogProps) {
    super(props);
    this.state = {
      busy: false,
      userInput: ''
    };
    this.siteRoleChanges = {
      siteRoles: {},
      serverAdmin: undefined
    };
  }

  private readonly importUsersCallback = () => {
    this.importUsers();
  }

  private readonly handleChangeCallback = (userInput: string) => {
    this.setState({ ...this.state, userInput });
  }

  private readonly siteRoleChangesCallback = (siteRoleChanges: ISiteRoleChanges) => {
    this.siteRoleChanges = siteRoleChanges;
  }

  public render() {
    return (
      <div className="tb-add-ad-users-dialog tb-react-modal">
        <div className="tb-dialog-title">{Messages.addAdUsersTitle()}</div>
        <div>
          {Messages.addAdUsersDesc()}
          <div className="tb-dialog-body-control tb-multiline-text-holder">
            <MultilineTextField
              handleChange={this.handleChangeCallback}
              disabled={this.state.busy}
            />
          </div>
          <div className="tb-picker-container tb-picker-border">
            <SiteRolesPickerContainer
              changes={this.siteRoleChangesCallback}
              busy={this.state.busy}
              angularContext={this.angularContext}
            />
          </div>
          <div className="tb-dialog-actions">
            <ButtonWithSpinner
              buttonType={ButtonType.Outline}
              clicked={this.props.dismiss}
              testId={'confirm-action-dialog-cancel'}
              title={Messages.cancel()}
              disabled={this.state.busy}
            />
            <span className="tb-lr-padded">
              <ButtonWithSpinner
                buttonType={ButtonType.Go}
                showSpinner={this.state.busy}
                clicked={this.importUsersCallback}
                testId={'confirm-action-dialog-submit'}
                title={Messages.importUsers_Submit()}
                disabled={this.state.busy || this.state.userInput.length === 0}
              />
            </span>
          </div>
        </div>
      </div>
    );
  }

  // Show notifications/makes api calls using angular
  public importUsers(): void {
    this.setState({ busy: true });

    let usersToAdd = UsernameHelpers.splitUsernames(this.state.userInput); // match strings delimited by semicolons & commas
    this.serverUsers
      .importUsersFromActiveDirectory(usersToAdd)
      .then((result: IImportUsersFromActiveDirectoryResult) => {
        // DO NOT SET STATE in this then clause
        const users: Set<IUser> = Set.fromIdItemArray(result.users);
        if (this.shouldAddUserToServerOnly(this.siteRoleChanges.serverAdmin, this.siteRoleChanges.siteRoles)) {
          this.addUserToServerOnly(users, result);
        } else {
          this.setUserSiteRoleForSites(users, result, this.siteRoleChanges.serverAdmin, this.siteRoleChanges.siteRoles);
        }
      });
  }

  // DO NOT SET STATE in here
  public shouldAddUserToServerOnly(serverAdmin: CheckboxState, siteRoles: _.Dictionary<ISiteRoleChange>): boolean {
    return !serverAdmin && _.isEmpty(siteRoles);
  }

  // DO NOT SET STATE in here
  public addUserToServerOnly(users: Set<IUser>, result: IImportUsersFromActiveDirectoryResult) {
    // if no site membership or server admin has been selected, toast for adding the users to the server
    let userActionParams: UserActionNotificationParams = {
      users: users,
      results: result.results,
      errors: result.errors,
      successCode: '10'
    };
    this.userActionNotification.notify(userActionParams);
    this.props.close({});
  }

  // DO NOT SET STATE in here
  public setUserSiteRoleForSites(users: Set<IUser>, result: IImportUsersFromActiveDirectoryResult, serverAdmin: CheckboxState, siteRoles: _.Dictionary<ISiteRoleChange>) {
    this.setRoles(users, serverAdmin, siteRoles).then(() => {
      this.showSiteRoleErrorsAndCloseDialog(users, result);
    });
  }

  // DO NOT SET STATE in here
  public showSiteRoleErrorsAndCloseDialog(users: Set<IUser>, result: IImportUsersFromActiveDirectoryResult) {
    if (result.errors && result.errors.length > 0) {
      // if there were any errors creating the users on the server, we need to notify about those.
      this.userActionNotification.notifyErrors({
        users: users,
        errors: result.errors
      });
    }
    this.props.close({});
  }

  /**
   * adds the users that were sucessfully created on the server to any sites and roles (including server admin)
   * that were selected in the site roles picker. DO NOT SET STATE in here
   */
  public setRoles(users: Set<IUserInfo>, serverAdmin: CheckboxState, siteRoles: _.Dictionary<ISiteRoleChange>): ng.IPromise<any> {
    if (users.isEmpty()) {
      return this.$q.when({});
    } else if (serverAdmin) {
      return this.updateServerAdmin(users, serverAdmin);
    } else if (!_.isEmpty(siteRoles)) {
      return this.updateSiteMembership(users, siteRoles);
    }
  }

  // section: update server admin
  // DO NOT SET STATE in here
  public updateServerAdmin(users: Set<IUserInfo>, serverAdmin: CheckboxState): ng.IPromise<any> {
    const newAdminState = serverAdmin === CheckboxState.checked;
    return this.rolesPickerClient.updateServerAdmin(users, newAdminState);
  }

  // section: update site membership or roles
  // DO NOT SET STATE in here
  public updateSiteMembership(users: Set<IUserInfo>, siteRoles: _.Dictionary<ISiteRoleChange>): ng.IPromise<any> {
    const userIds = users.keys();
    const params = this.rolesPickerClient.siteRoleChangesToParams(
      userIds,
      siteRoles
    );
    return this.rolesPickerClient
      .updateServerUsersSiteMembership(params)
      .then((result: IUpdateServerUsersSiteMembershipResult) => {
        if (result.results) {
          this.siteMembershipUpdateActionNotification.notify(
            result.results,
            result.errors,
            users,
            siteRoles
          );
        }
      });
  }
}
